import React, { useEffect, useRef, useState } from 'react';
import { Typography, Link, useMediaQuery, Stack, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Else, If, Then } from 'react-if';
import { useHistory } from "react-router-dom";
import { ArrowForward } from '@mui/icons-material';
import CustomSlider from '../../Components/CustomSlider';
import FormBuilder from '../../Components/FormBuilder';
import display from '../../lang';
import { useDispatch, useSelector } from 'react-redux';
import { post, showError } from '../../AppRedux/Actions';
import { login } from '../../AppRedux/Actions/Auth';
import CustomButton from '../../Components/CustomButton';
import { CHECK_SUB_DOMAIN } from '../../constant';

const LoginPage = (props) => {
    const theme = useTheme();
    const history = useHistory();
    const dispatch = useDispatch();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    // Component state management
    const [counterDisplay, setCounterDisplay] = useState('Resend OTP');
    const [showOTP, setShowOTP] = useState(false);
    const [values, setValues] = useState({});
    const intervalId = useRef(null);
    const [data, setData] = useState({});
    const [formFields, setFormFields] = useState([]);
    const [otp_info, setOtpInfo] = useState('');

    const { loading } = useSelector((state) => state.common);

    useEffect(() => {
        checksubdomain();
        return () => {
            if (intervalId.current) {
                clearInterval(intervalId.current);
                setValues({});
            }
        }
    }, []);

    // component functions
    const checksubdomain = () => {
        post(dispatch, '', CHECK_SUB_DOMAIN, { host_name: String(window.location.origin), form: 'login_form' }, {}, (data = {}) => {
            setData(data || {});
            if (Object.hasOwnProperty.call(data, 'form_data')) {
                const formArr = [], defaultFormValues = {};
                for (let key in data.form_data) {
                    const element = data.form_data[key];
                    defaultFormValues[key] = '';
                    if (Object.hasOwnProperty.call(element, 'value')) {
                        defaultFormValues[key] = element.value ?? '';
                        delete element.value;
                    }
                    formArr.push(element);
                }
                setFormFields(formArr);
                setValues((pre) => ({ ...pre, ...defaultFormValues }));
            }
        });
    }

    // otp timer handler
    const init_counter = () => {
        if (intervalId.current) {
            clearInterval(intervalId.current);
        }
        let tic_count = 60;
        setCounterDisplay(`${tic_count--} ${display('Seconds')}`);
        if (!showOTP) {
            setShowOTP(() => true);
        }
        intervalId.current = setInterval(() => {
            setCounterDisplay(`${tic_count--} ${display('Seconds')}`);
            if (tic_count < 0) {
                setCounterDisplay(() => 'Resend OTP');
                clearInterval(intervalId.current);
            }
        }, 1000);
    };

    const checkAllFields = () => {
        for (let element of formFields) {
            if (element.required && !values[element.name] && element.type != 'hidden') {
                showError(dispatch, `${element.label} is Required.`);
                return false;
            }

            if (element.name == 'customer_id' && values[element.name] && values[element.name]?.length < 0) {
                showError(dispatch, `${element.label} in wrong format.`);
                return false;
            }
        }
        return true;
    }

    // Form submit handler
    const handleSubmit = (event = {}) => {
        event.preventDefault();
        if (checkAllFields()) {
            if (showOTP && (!Object.hasOwnProperty.call(values, 'otp') || (values.otp || "").length < 6)) {
                showError(dispatch, display("INVALID_OTP"));
            } else if (values.password.trim() === '' || values.username.trim() === '') {
                showError(dispatch, display("INCORRECT_PASSWORD"));
            } else {
                dispatch(login(values, history, (data) => {
                    if (Object.hasOwnProperty.call(data, 'show_otp_input') && data.show_otp_input) {
                        setFormFields((pre) => pre.map((item) => item.name == 'otp' ? { ...item, type: 'input' } : item));
                        init_counter();
                        setOtpInfo(data.info || '');
                    }
                }));
            }
        }
    };

    const resentOTPHandler = () => {
        if (checkAllFields()) {
            if (values.password.trim() === '' || values.username.trim() === '') {
                showError(dispatch, display("INCORRECT_PASSWORD"));
            } else {
                dispatch(login({ ...values, resend_otp: true }, history, (data) => {
                    if (Object.hasOwnProperty.call(data, 'show_otp_input') && data.show_otp_input) {
                        init_counter();
                        setOtpInfo(data.info || '');
                    }
                }));
            }
        }
    };

    const formChangeHandler = (event = {}) => {
        const { value, name } = event.target || {};
        if (name == 'login_type') {
            setFormFields((pre) => pre.map((item) => item.name == 'customer_id' ? { ...item, type: (value == 'email_address' && (!item.options || item.options?.length == 0)) ? 'hidden' : item.options?.length > 0 ? 'select' : 'input' } : item));
        }
        setValues((pre) => ({ ...pre, [name]: value }));
    };

    const handleForgotPasswordClick = () => {
        history.push('/forgot-password');
    };

    return (
        <Stack component="main" sx={{ height: '100vh', width: '100%', userSelect: 'none' }} flexDirection={'row'}>
            {/* left Side - Images (hidden on small screens) */}
            <If condition={!isSmallScreen}>
                <Then>
                    <Box width={'65%'}>
                        <CustomSlider orgWithCompleteLayout={data?.LANDING_PAGE_DATA?.orgWithCompleteLayout} sliderArray={data?.LANDING_PAGE_DATA?.orgWithCompleteLayout && data?.LANDING_PAGE_DATA?.slider_content} />
                    </Box>
                </Then>
            </If>
            {/* Right Side - Input Fields */}
            <If condition={Object.values(data).length != 0}>
                <Then>
                    <Stack
                        flex={1}
                        justifyContent={'center'}
                        alignContent={'center'}
                        padding={8}
                        gap={3}
                        position={'relative'}
                    >
                        <If condition={formFields.length > 0}>
                            <Then>
                                <img src={data?.CLIENT_LOGO || "/img/svg/Logo.svg"} loading="lazy" alt={"logo"} style={{ maxWidth: '100px', color: 'black', position: 'absolute', top: '40px', right: '70px' }} />
                                <Typography variant="h1" sx={{ fontSize: "1.75rem", fontWeight: 'bolder' }} id="body">
                                    Login
                                </Typography>
                                <form onSubmit={handleSubmit}>
                                    <FormBuilder fields={formFields} values={values} inputHandler={formChangeHandler} size='sm'>
                                        <If condition={showOTP}>
                                            <Then>
                                                <Typography variant='caption'> {otp_info} </Typography>
                                                <CustomButton sx={{ mb: 1 }} variant="contained" color="primary" fullWidth onClick={resentOTPHandler} disabled={counterDisplay === 'Resend OTP' ? false : true}>
                                                    {counterDisplay}
                                                </CustomButton>
                                            </Then>
                                        </If>
                                        <Stack mt={-1} width={'100%'} flexDirection={'row'} justifyContent={'flex-end'}>
                                            <Link underline='none' style={{ cursor: 'pointer' }} textAlign={'right'} onClick={handleForgotPasswordClick} variant="body2">
                                                Forgot password?
                                            </Link>
                                        </Stack>
                                        <CustomButton sx={{ mt: -1 }} onClick={handleSubmit} disabled={loading} type="submit" fullWidth variant="contained" color="primary" endIcon={<ArrowForward />}>
                                            Login
                                        </CustomButton>
                                    </FormBuilder>
                                </form>
                                {/* okta login */}
                                <If condition={data.okta_enabled}>
                                    <Then>
                                        <CustomButton
                                            // sx={{ mt: -1 }}
                                            variant="outlined"
                                            color="gray"
                                            fullWidth
                                            disableElevation
                                            type='link'
                                            target='_blank'
                                            href={data.okta_login_url}
                                        >
                                            Login With Okta
                                        </CustomButton>
                                    </Then>
                                </If>
                                {/* sso and okta login setup */}
                                <If condition={data.sso_enabled}>
                                    <Then>
                                        <CustomButton
                                            sx={{ mt: -1 }}
                                            variant="outlined"
                                            color="gray"
                                            fullWidth
                                            disableElevation
                                            onClick={() => {
                                                history.push("auth/login-sso");
                                            }}
                                        >
                                            Login With SSO
                                        </CustomButton>
                                    </Then>
                                </If>
                                {/* app store display */}
                                <Stack flexDirection={'row'} justifyContent={'center'} gap={5}>
                                    <a target='_blank' href="https://apps.apple.com/us/app/zimyo/id1579008301">
                                        <img
                                            src="/img/svg/AppStore.svg"
                                            alt="app store"
                                            style={{ height: "auto", maxWidth: "100%", userSelect: 'none', cursor: 'pointer' }}
                                        />
                                    </a>
                                    <a target="_blank" href="https://play.google.com/store/apps/details?id=com.zimyo.hrms">
                                        <img
                                            src="/img/svg/PlayStore.svg"
                                            alt="app store"
                                            style={{ height: "auto", maxWidth: "100%", userSelect: 'none', cursor: 'pointer' }}
                                        />
                                    </a>
                                </Stack>
                            </Then>
                            <Else>
                                <Typography>Opps Something went wrong. Please try later.</Typography>
                            </Else>
                        </If>
                    </Stack>
                </Then>
            </If>

        </Stack >
    );
};

export default LoginPage;